import React from 'react';
import { Link } from 'react-router-dom';
import { ArrayHelpers, Help, Paging, StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import BlobImage from 'components/blobImage';
import { useSelector } from 'react-redux';
import { benchmarkColour } from 'helpers/metrics';
import posthog from 'posthog-js';

export const getGroupIcon = type => {
    switch (type) {
        case 'B':
            return 'fa-cubes';
        case 'D':
            return 'fa-building';
        case 'L':
            return 'fa-map-marker';
        case 'R':
            return 'fa-address-card';
        case 'T':
        case 'X':
            return 'fa-users';
        default:
            return 'fa-user';
    }
};

export const Board = ({ data, item, id }) => {
    const loaded = React.useRef(null);
    const [sortedData, setSortedData] = React.useState([]);
    const [pagedData, setPagedData] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(6);
    const [desc, setDesc] = React.useState(true);
    const recordCount = data.length;

    const metrics = useSelector(state => state.dashboard.metrics);
    const goals = useSelector(state => state.dashboard.goals);
    const user = useSelector(state => state.user.user);
    const parsed = JSON.parse(item.data);
    const formatMetric = React.useCallback(
        value => {
            const itemMetric = parsed.metric1;
            const type = parsed.type1;
            if (type === 'G') return StringHelpers.formatMetric('P', value);
            if (type === 'C') return StringHelpers.formatMetric('R', value);
            if (parsed.type) return value.toFixed(1);
            const metric =
                itemMetric &&
                metrics.find(
                    m =>
                        (m.realTime && itemMetric.metricRef === `RT-${m.id}`) ||
                        (itemMetric.metricRef.indexOf('RT-') < 0 &&
                            (m.ref || m.integration) === itemMetric.type &&
                            (m.id === itemMetric.metricId || m.id === itemMetric.metricRef))
                );

            return StringHelpers.formatMetric(metric?.format || 'Q', value, user.currencySymbol);
        },
        [item, metrics]
    );

    const goalFormat = React.useCallback(
        id => {
            const goal = goals.find(g => g.id === id);
            if (!goal) return 'Q';
            const metric = metrics.find(m => !m.realTime && m.id === goal.metric_Id && m.integration === goal.integration);
            if (!metric) return 'Q';
            return metric.format;
        },
        [goals, metrics]
    );

    const highIsGood = React.useCallback(() => {
        if (parsed.type1 !== 'M') return true; // competencies and goals always high = good
        const itemMetric = parsed.metric1;
        const metric =
            itemMetric &&
            metrics.find(
                m =>
                    (m.realTime && itemMetric.metricRef === `RT-${m.id}`) ||
                    (itemMetric.metricRef.indexOf('RT-') < 0 &&
                        (m.ref || m.integration) === itemMetric.type &&
                        (m.id === itemMetric.metricId || m.id === itemMetric.metricRef))
            );

        return metric?.high_Good;
    }, [item, metrics]);

    React.useEffect(() => {
        const pageSizeGenerator = () => {
            const rowHeight = 41;
            const extrasHeight = 60;
            const me = document.querySelector(`#${id}`);
            let result = me ? ((me.offsetHeight - extrasHeight) / rowHeight).toFixed() : 10;
            if (result < recordCount) result--;
            if (recordCount && !result) result = 1;
            return result < 0 ? 0 : result;
        };

        const ps = pageSizeGenerator();

        setPagedData(ArrayHelpers.getPage(sortedData, page, ps));
        setPageSize(ps);
    }, [id, item.h, page, sortedData]);

    React.useEffect(() => {
        setPage(1);
    }, [item.h]);

    React.useEffect(() => {
        const highGood = highIsGood();

        let temp = [...data].sort((a, b) => {
            const realA = highGood ? a : b;
            const realB = highGood ? b : a;

            if (realA.value === realB.value) {
                if (realA.full_Name !== undefined && realB.full_Name !== undefined) {
                    return realA.full_Name.localeCompare(realB.full_Name); // Sort by name
                }
            }

            return desc ? realB.value - realA.value : realA.value - realB.value; // Sort by value
        });

        setSortedData(temp.map((x, i) => ({ ...x, position: i + 1 })));
    }, [desc, data, highIsGood]);

    const groupIcon = getGroupIcon(parsed.groupBy || 'P');

    if (pagedData && pagedData.length === 0) {
        return <div>{SystemText('Dashboard/Widgets/Leaderboard/NoResults')}</div>;
    }

    return (
        <div ref={loaded}>
            <table className="table table-condensed" style={{ marginBottom: '15px' }}>
                <tbody>
                    <tr>
                        <th width="1">#</th>
                        <th>{SystemText(`Dashboard/Widgets/Leaderboard/${parsed.groupBy || 'P'}`)}</th>
                        <th width="1">
                            <div className="pointer flex flex-center" onClick={() => setDesc(!desc)}>
                                <span className="flex-grow" />
                                <i
                                    data-testid="button-asc"
                                    className={`pointer fa fa-caret-up ${desc ? 'text-sm' : 'text-lg'}`}
                                />
                                <i
                                    data-testid="button-desc"
                                    className={`pointer fa fa-caret-down ${!desc ? 'text-sm' : 'text-lg'}`}
                                />
                            </div>
                        </th>
                    </tr>
                    {pagedData.map((t, i) => {
                        const label = (
                            <label className={`label label-${benchmarkColour(t.percentile, !highIsGood())}`}>
                                {formatMetric(t.value)}
                            </label>
                        );

                        const details = (
                            <div className="flex flex-center">
                                {['P', 'M'].some(x => x === (parsed.groupBy || 'P')) ? (
                                    <BlobImage
                                        height="30"
                                        className="img-circle mr10"
                                        folder="avatar"
                                        initials={t.initials}
                                        key={t.avatar}
                                        src={t.avatar}
                                        alt={t.full_Name}
                                    />
                                ) : (
                                    <i className={`fa ${groupIcon} mr10`} />
                                )}
                                <div style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {t.full_Name}
                                </div>
                            </div>
                        );

                        return (
                            <tr key={t.id}>
                                <td className="no-wrap" style={null}>
                                    {t.position}.
                                </td>
                                <td className="no-wrap">
                                    {['P', 'M'].some(x => x === (parsed.groupBy || 'P')) ? (
                                        <Link
                                            onClick={() => posthog.capture('widget-profile-click')}
                                            to={`/develop/performance-dashboard/${t.id}`}
                                            className="flex flex-center"
                                        >
                                            {details}
                                        </Link>
                                    ) : (
                                        details
                                    )}
                                </td>
                                <td style={null} className="no-wrap text-right">
                                    {t.quota ? (
                                        <Help id={`quota-${t.id}-${id}`} target={label}>
                                            {StringHelpers.formatMetric(
                                                goalFormat(parsed.goal1),
                                                t.achieved,
                                                user.currencySymbol
                                            )}{' '}
                                            /{' '}
                                            {StringHelpers.formatMetric(
                                                goalFormat(parsed.goal1),
                                                t.quota,
                                                user.currencySymbol
                                            )}
                                        </Help>
                                    ) : (
                                        label
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {recordCount > pageSize && (
                <div className="text-center mt10">
                    <Paging
                        page={page}
                        narrow={item.w < 3}
                        pageSize={pageSize}
                        recordCount={recordCount}
                        goToPage={setPage}
                    />
                </div>
            )}
        </div>
    );
};

export default Board;
